import { ArrowRight } from "lucide-react";
import { useSeo } from "@/lib/site";
import { slug } from "@/data/nav";
import { PageHero, ImageVisual } from "@/components/site/PageHero";
import { SphereVisual, AnswerVisual } from "@/components/site/Visuals";
import { Section, Statement, Label, H2, Paras, Bullets, ServiceBlocks, Process, MetricsCloud, CtaBand, CtaButton, CtaRow } from "@/components/site/Blocks";
import { Reveal } from "@/components/site/Motion";

const line = (l) => (typeof l === "string" ? l : <span className="font-serif-i text-gradient">{l.i}</span>);

const Ctas = ({ items, dark, prefix }) => (
  <CtaRow className="mt-10">
    {items.map((c, i) => (
      <CtaButton key={c.label} label={c.label} to={c.to} href={c.href} testid={`${prefix}-cta-${slug(c.label)}`}
        variant={dark ? (i ? "ghost" : "primary") : i ? "outline" : "dark"} />
    ))}
  </CtaRow>
);

const Visual = ({ v }) => {
  if (v.sphere) return <SphereVisual caption={v.caption} />;
  if (v.answer) return <AnswerVisual />;
  return <ImageVisual src={v.img} alt={v.alt} caption={v.caption} />;
};

const Journey = ({ steps }) => (
  <ol className="mt-10 space-y-2" data-testid="journey-steps">
    {steps.map((s, i) => (
      <Reveal as="li" key={s} delay={i * 0.08} y={12} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4">
        <span className="font-mono text-xs text-violet-700">{String(i + 1).padStart(2, "0")}</span>
        <span className="flex-1 font-display text-lg text-slate-900">{s}</span>
        {i < steps.length - 1 && <ArrowRight className="h-4 w-4 text-slate-400" />}
      </Reveal>
    ))}
  </ol>
);

const ListSection = ({ s, n, tone }) => (
  <Section tone={tone} testid={`section-${slug(s.title)}`}>
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Label n={n}>{s.label}</Label></Reveal>
        <Reveal delay={0.05}><H2 className="mt-6">{s.title}</H2></Reveal>
        <Reveal delay={0.1}><Paras className="mt-6" items={s.paras} /></Reveal>
      </div>
      <div className="lg:col-span-6 lg:col-start-7">
        <Reveal><Bullets items={s.bullets} /></Reveal>
        {s.outro && <Reveal delay={0.1}><p className="mt-10 font-serif-i text-2xl leading-snug text-violet-800 sm:text-3xl">{s.outro}</p></Reveal>}
        {s.ctas && <Ctas items={s.ctas} prefix={slug(s.title)} />}
      </div>
    </div>
  </Section>
);

const Trio = ({ s, n, tone }) => (
  <Section tone={tone} testid={`section-${slug(s.title)}`}>
    <div className="grid gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <Reveal><Label n={n}>{s.label}</Label></Reveal>
        <Reveal delay={0.05}><H2 className="mt-6">{s.title}</H2></Reveal>
        <Reveal delay={0.1}><Paras className="mt-6" items={s.paras} /></Reveal>
        {s.ctas && <Ctas items={s.ctas} prefix={slug(s.title)} />}
      </div>
      <div className="grid gap-3 lg:col-span-6 lg:col-start-7">
        {s.items.map(([k, v], i) => (
          <Reveal key={k} delay={i * 0.08} className="glow-card flex items-center gap-6 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
            <span className="font-display text-4xl font-semibold text-gradient-dark sm:text-5xl">{k}</span>
            <span className="text-[15px] leading-relaxed text-slate-700">{v}</span>
          </Reveal>
        ))}
      </div>
    </div>
  </Section>
);

const renderSection = (s, i, tone) => {
  const n = String(i + 1).padStart(2, "0");
  const key = s.title;
  const testid = `section-${slug(s.title)}`;
  switch (s.type) {
    case "statement": {
      const aside = (
        <>
          {s.chips && <div className="mt-8 flex flex-wrap gap-2">{s.chips.map((c) => <span key={c} className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm text-slate-800">{c}</span>)}</div>}
          {s.ctas && <Ctas items={s.ctas} dark={s.dark} prefix={slug(s.title)} />}
        </>
      );
      return <Statement key={key} n={n} label={s.label} title={s.title} lead={s.lead} paras={s.paras} dark={s.dark} tone={s.dark ? "plum" : tone} aside={aside} testid={testid} />;
    }
    case "journey":
      return <Statement key={key} n={n} label={s.label} title={s.title} paras={s.paras} tone={tone} aside={<Journey steps={s.steps} />} testid={testid} />;
    case "covers":
      return (
        <Section key={key} tone="subtle" testid={testid}>
          <Reveal><Label n={n}>{s.label}</Label></Reveal>
          <Reveal delay={0.05}><H2 className="mt-6">{s.title}</H2></Reveal>
          <ServiceBlocks items={s.items} testid="service-covers" />
        </Section>
      );
    case "list":
      return <ListSection key={key} s={s} n={n} tone={tone} />;
    case "trio":
      return <Trio key={key} s={s} n={n} tone={tone} />;
    case "measure":
      return <MetricsCloud key={key} n={n} title={s.title} intro={s.intro || ["Depending on your objectives, we can measure:"]} items={s.items} outro={s.outro} />;
    case "process":
      return <Process key={key} n={n} title={s.title} steps={s.steps} />;
    default:
      return null;
  }
};

export default function ServicePage({ page }) {
  useSeo(page.seoTitle, page.meta);
  let light = 0;
  return (
    <div data-testid={page.testid}>
      <PageHero
        crumb={page.crumb}
        title={page.title.map(line)}
        sub={page.sub}
        paras={page.paras}
        ctas={page.ctas.map((c, i) => <CtaButton key={c.label} label={c.label} to={c.to} href={c.href} variant={i ? "ghost" : "primary"} testid={i ? "hero-secondary-cta" : "hero-primary-cta"} />)}
        visual={<Visual v={page.visual} />}
      />
      {page.sections.map((s, i) => {
        const isLight = !s.dark && ["statement", "journey", "list", "trio"].includes(s.type);
        const tone = s.tone || (isLight ? (light++ % 2 ? "subtle" : undefined) : undefined);
        return renderSection(s, i, tone);
      })}
      <CtaBand
        title={page.cta.title.map(line)}
        paras={page.cta.paras}
        ctas={page.cta.ctas.map((c, i) => <CtaButton key={c.label} label={c.label} to={c.to} href={c.href} variant={i ? "ghost" : "primary"} testid={i ? "final-secondary-cta" : "final-primary-cta"} />)}
      />
    </div>
  );
}
